import { useState, useRef, useEffect } from 'react'
import { Search, X, UserPlus, Building2, Mail } from 'lucide-react'
import { useClientSearch } from '@/hooks/useClientSearch'
import { cn } from '@/lib/utils'

interface SelectedClient {
  id: string
  email: string
  full_name: string | null
  company: string | null
}

interface ClientSelectorProps {
  onSelect: (client: SelectedClient) => void
  onSwitchToManual: () => void
  selectedClient: SelectedClient | null
  onClear: () => void
}

export function ClientSelector({ onSelect, onSwitchToManual, selectedClient, onClear }: ClientSelectorProps) {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const { data: clients = [], isLoading } = useClientSearch(search)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (selectedClient) {
    return (
      <div className="flex items-center gap-3 p-3 bg-mint-50 border border-mint-200 rounded-lg">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-brown-900 truncate">
            {selectedClient.full_name || selectedClient.email}
          </p>
          {selectedClient.company && (
            <p className="text-sm text-brown-600 flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              {selectedClient.company}
            </p>
          )}
          <p className="text-sm text-brown-500 flex items-center gap-1">
            <Mail className="w-3 h-3" />
            {selectedClient.email}
          </p>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="p-1 hover:bg-mint-100 rounded-full"
        >
          <X className="w-4 h-4 text-brown-500" />
        </button>
      </div>
    )
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brown-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => search.length >= 2 && setIsOpen(true)}
          placeholder="Rechercher un client (nom, email, entreprise)..."
          className="w-full pl-10 pr-4 py-2.5 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 text-sm"
        />
      </div>

      {isOpen && search.length >= 2 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-beige-200 rounded-lg shadow-lg max-h-64 overflow-auto">
          {isLoading ? (
            <div className="p-3 text-center text-sm text-brown-500">
              Recherche...
            </div>
          ) : clients.length > 0 ? (
            <>
              {clients.map((client) => (
                <button
                  key={client.id}
                  type="button"
                  className="w-full px-4 py-3 text-left hover:bg-beige-50 transition border-b border-beige-100 last:border-b-0"
                  onClick={() => {
                    onSelect(client)
                    setSearch('')
                    setIsOpen(false)
                  }}
                >
                  <p className="font-medium text-brown-900 text-sm">
                    {client.full_name || 'Sans nom'}
                    {client.company && (
                      <span className="text-brown-500 font-normal"> - {client.company}</span>
                    )}
                  </p>
                  <p className="text-xs text-brown-500">{client.email}</p>
                </button>
              ))}
              <button
                type="button"
                className="w-full px-4 py-3 text-left hover:bg-beige-50 transition flex items-center gap-2 text-mint-700"
                onClick={() => {
                  onSwitchToManual()
                  setIsOpen(false)
                }}
              >
                <UserPlus className="w-4 h-4" />
                <span className="text-sm font-medium">Nouveau client</span>
              </button>
            </>
          ) : (
            <>
              <div className="p-3 text-center text-sm text-brown-500">
                Aucun client trouvé
              </div>
              <button
                type="button"
                className="w-full px-4 py-3 text-left hover:bg-beige-50 transition flex items-center gap-2 text-mint-700 border-t border-beige-100"
                onClick={() => {
                  onSwitchToManual()
                  setIsOpen(false)
                }}
              >
                <UserPlus className="w-4 h-4" />
                <span className="text-sm font-medium">Nouveau client</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
